import { z } from 'zod';
import { AddressSchema, AuditSchema, IdSchema } from './common.ts';
import { AccountingMethodSchema } from './bill.ts';
import { SalesTaxExemptionSchema } from './tax-exemption.ts';

/** Which days the company normally works, before per-job exceptions. */
export const WorkWeekSchema = z.object({
  monday: z.boolean(),
  tuesday: z.boolean(),
  wednesday: z.boolean(),
  thursday: z.boolean(),
  friday: z.boolean(),
  saturday: z.boolean(),
  sunday: z.boolean(),
});
export type WorkWeek = z.infer<typeof WorkWeekSchema>;

export const DEFAULT_WORK_WEEK: WorkWeek = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

export const CompanySchema = AuditSchema.extend({
  id: IdSchema,
  name: z.string().min(1),
  logoFileId: IdSchema.optional(),
  address: AddressSchema.optional(),
  phone: z.string().optional(),
  email: z.string().email().optional(),
  website: z.string().optional(),
  /**
   * Which model the assistant answers with (SCRUM-223).
   *
   * On the company because it is the company's preference — a cheaper
   * model for routine questions, a stronger one when somebody is working
   * through something. **The API key is not here and must never be**: it
   * is Buildmate's own credential, it lives in Secret Manager, and
   * anything in this document is readable by whoever can read the
   * company. The monthly allowance is not here either, for the matching
   * reason — a company that could raise its own cap would be setting its
   * own bill, so that lives in `settings/assistant`, which no client may
   * write.
   */
  assistantModel: z.string().max(100).optional(),
  timeZone: z.string().default('America/Phoenix'),
  currency: z.string().length(3).default('USD'),
  workWeek: WorkWeekSchema.default(DEFAULT_WORK_WEEK),
  /**
   * What "actual cost" means for this company's job costing (P5-10a).
   *
   * **Accrual** counts every bill, because a job that has taken delivery
   * has spent the money whether or not the cheque has gone out. **Cash**
   * counts only what has been paid. The two answer different questions and
   * a reader has to know which one they are looking at, which is why it is
   * a company setting rather than a toggle on the page — a figure that
   * changes meaning when somebody flicks a switch is a figure nobody can
   * quote.
   *
   * Defaults to accrual, which is what most builders' own reports use and
   * what `docs/features/07-costing-budget.md` §7 names as the default.
   * Absent on companies created before this existed, and the default is
   * what they get rather than an error.
   */
  accountingMethod: AccountingMethodSchema.default('accrual'),
  /**
   * The company's sales-tax exemption certificate (SCRUM-312).
   *
   * On the company because that is whose exemption it is: a bill records
   * that it was *bought* under the exemption, and this is the thing being
   * claimed. Keeping the number on each bill instead would mean a
   * renewal rewrote history, and a mistyped digit would differ from bill
   * to bill with nothing to compare it against.
   *
   * Optional, and absent is the honest default — most companies do not
   * have one, and inventing a blank certificate would make "do we have
   * an exemption" unanswerable. See `tax-exemption.ts` for why this is
   * not a credential and why selling exempt is a separate thing.
   */
  salesTaxExemption: SalesTaxExemptionSchema.optional(),
});
export type Company = z.infer<typeof CompanySchema>;
